<template>
    <app-dialog
        :open="open"
        :title="title"
        size="md"
        header-variant="sectioned"
        panel-class="h-[520px]"
        body-class="flex min-h-0 flex-1 flex-col p-0"
        @close="$emit('close')"
    >
                    <div v-if="showTabBar" class="stage-header-surface border-b border-ultralight px-5">
                        <div class="flex items-center gap-8">
                            <button
                                type="button"
                                class="py-4 text-base font-medium border-b-2 transition-colors"
                                :class="activeTab === 'internal' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-700'"
                                @click="activeTab = 'internal'"
                            >
                                {{ internalTabLabel }}
                            </button>
                            <button
                                v-if="showCustomerTab"
                                type="button"
                                class="py-4 text-base font-medium border-b-2 transition-colors"
                                :class="activeTab === 'customer' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-700'"
                                @click="activeTab = 'customer'"
                            >
                                {{ customerTabLabel }}
                            </button>
                            <button
                                v-if="showGuestTab"
                                type="button"
                                class="py-4 text-base font-medium border-b-2 transition-colors"
                                :class="activeTab === 'guest' ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-700'"
                                @click="activeTab = 'guest'"
                            >
                                {{ guestTabLabel }}
                            </button>
                        </div>
                    </div>
                    <div class="flex-1 min-h-0 overflow-y-auto">
                        <div v-if="activeTab === 'internal'">
                            <template v-if="internalUsers.length > 0">
                                <component
                                    :is="permissionMode ? 'div' : 'label'"
                                    v-for="user in internalUsers"
                                    :key="'approval-internal-' + user.id"
                                    class="flex items-center px-6 py-3 border-b border-ultralight"
                                    :class="permissionMode ? '' : (isUserSelectable(user) ? 'cursor-pointer' : 'cursor-not-allowed')"
                                >
                                    <input
                                        v-if="!permissionMode"
                                        v-model="draftInternalIds"
                                        :value="Number(user.id)"
                                        class="form-checkbox h-4 w-4 text-gray-600 transition duration-150 ease-in-out mr-4"
                                        type="checkbox"
                                        :disabled="!isUserSelectable(user)"
                                    >
                                    <img
                                        :alt="user.name || 'Avatar'"
                                        :src="approvalUserAvatar(user)"
                                        class="h-6 w-6 rounded-full object-cover mr-4"
                                    >
                                    <div class="min-w-0 flex-1">
                                        <span
                                            class="block text-sm font-medium truncate"
                                            :class="isUserSelectable(user) ? 'text-gray-800' : 'text-gray-400'"
                                        >
                                            {{ user.name }}
                                        </span>
                                    </div>
                                    <span
                                        v-if="user.disabled_hint"
                                        class="ml-4 flex-shrink-0 text-xs text-gray-400 text-right"
                                    >
                                        {{ user.disabled_hint }}
                                    </span>
                                    <div v-if="permissionMode" class="ml-6 flex flex-shrink-0 items-center gap-6">
                                        <label class="inline-flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                                            <span>{{ readLabel }}</span>
                                            <input
                                                class="form-checkbox h-4 w-4 text-gray-600 transition duration-150 ease-in-out"
                                                type="checkbox"
                                                :checked="hasReadPermission('internal', user.id)"
                                                :disabled="!isUserSelectable(user)"
                                                @change="setReadPermission('internal', user.id, $event.target.checked)"
                                            >
                                        </label>
                                        <label class="inline-flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                                            <span>{{ editLabel }}</span>
                                            <input
                                                class="form-checkbox h-4 w-4 text-gray-600 transition duration-150 ease-in-out"
                                                type="checkbox"
                                                :checked="hasEditPermission('internal', user.id)"
                                                :disabled="!isUserSelectable(user)"
                                                @change="setEditPermission('internal', user.id, $event.target.checked)"
                                            >
                                        </label>
                                    </div>
                                </component>
                            </template>
                            <div v-else class="px-6 py-6 text-sm text-gray-500">
                                {{ emptyInternalText }}
                            </div>
                        </div>
                        <div v-else-if="activeTab === 'customer'">
                            <template v-if="customerUsers.length > 0">
                                <component
                                    :is="permissionMode ? 'div' : 'label'"
                                    v-for="user in customerUsers"
                                    :key="'approval-customer-' + user.id"
                                    class="flex items-center px-6 py-3 border-b border-ultralight"
                                    :class="permissionMode ? '' : (isUserSelectable(user) ? 'cursor-pointer' : 'cursor-not-allowed')"
                                >
                                    <input
                                        v-if="!permissionMode"
                                        v-model="draftCustomerIds"
                                        :value="Number(user.id)"
                                        class="form-checkbox h-4 w-4 text-gray-600 transition duration-150 ease-in-out mr-4"
                                        type="checkbox"
                                        :disabled="!isUserSelectable(user)"
                                    >
                                    <img
                                        :alt="user.name || 'Avatar'"
                                        :src="approvalUserAvatar(user)"
                                        class="h-6 w-6 rounded-full object-cover mr-4"
                                    >
                                    <div class="min-w-0 flex-1">
                                        <span
                                            class="block text-sm font-medium truncate"
                                            :class="isUserSelectable(user) ? 'text-gray-800' : 'text-gray-400'"
                                        >
                                            {{ user.name }}
                                        </span>
                                    </div>
                                    <span
                                        v-if="user.disabled_hint"
                                        class="ml-4 flex-shrink-0 text-xs text-gray-400 text-right"
                                    >
                                        {{ user.disabled_hint }}
                                    </span>
                                    <div v-if="permissionMode" class="ml-6 flex flex-shrink-0 items-center gap-6">
                                        <label class="inline-flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                                            <span>{{ readLabel }}</span>
                                            <input
                                                class="form-checkbox h-4 w-4 text-gray-600 transition duration-150 ease-in-out"
                                                type="checkbox"
                                                :checked="hasReadPermission('customer', user.id)"
                                                :disabled="!isUserSelectable(user)"
                                                @change="setReadPermission('customer', user.id, $event.target.checked)"
                                            >
                                        </label>
                                        <label class="inline-flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                                            <span>{{ editLabel }}</span>
                                            <input
                                                class="form-checkbox h-4 w-4 text-gray-600 transition duration-150 ease-in-out"
                                                type="checkbox"
                                                :checked="hasEditPermission('customer', user.id)"
                                                :disabled="!isUserSelectable(user)"
                                                @change="setEditPermission('customer', user.id, $event.target.checked)"
                                            >
                                        </label>
                                    </div>
                                </component>
                            </template>
                            <div v-else class="px-6 py-6 text-sm text-gray-500">
                                {{ emptyCustomerText }}
                            </div>
                        </div>
                        <div v-else>
                            <template v-if="guestUsers.length > 0">
                                <label
                                    v-for="user in guestUsers"
                                    :key="'approval-guest-' + user.id"
                                    class="flex items-center px-6 py-3 border-b border-ultralight"
                                    :class="isUserSelectable(user) ? 'cursor-pointer' : 'cursor-not-allowed'"
                                >
                                    <input
                                        v-model="draftGuestIds"
                                        :value="Number(user.id)"
                                        class="form-checkbox h-4 w-4 text-gray-600 transition duration-150 ease-in-out mr-4"
                                        type="checkbox"
                                        :disabled="!isUserSelectable(user)"
                                    >
                                    <img
                                        :alt="user.name || 'Avatar'"
                                        :src="approvalUserAvatar(user)"
                                        class="h-6 w-6 rounded-full object-cover mr-4"
                                    >
                                    <div class="min-w-0 flex-1">
                                        <span
                                            class="block text-sm font-medium truncate"
                                            :class="isUserSelectable(user) ? 'text-gray-800' : 'text-gray-400'"
                                        >
                                            {{ user.name }}
                                        </span>
                                    </div>
                                    <span
                                        v-if="user.disabled_hint"
                                        class="ml-4 flex-shrink-0 text-xs text-gray-400 text-right"
                                    >
                                        {{ user.disabled_hint }}
                                    </span>
                                </label>
                            </template>
                            <div v-else class="px-6 py-6 text-sm text-gray-500">
                                {{ emptyGuestText }}
                            </div>
                        </div>
                    </div>
        <template #footer>
            <app-dialog-action :label="saveLabel" @click="emitSave" />
        </template>
    </app-dialog>
</template>

<script>
import AppDialog from '@/components/modals/app-dialog.vue';
import AppDialogAction from '@/components/modals/app-dialog-action.vue';

export default {
    name: 'approval-management-dialog',
    components: {AppDialog, AppDialogAction},
    props: {
        open: {
            type: Boolean,
            default: false,
        },
        balancedChrome: {
            type: Boolean,
            default: false,
        },
        permissionMode: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            default: 'Freigabeverwaltung',
        },
        showTabBar: {
            type: Boolean,
            default: true,
        },
        internalUsers: {
            type: Array,
            default: () => [],
        },
        customerUsers: {
            type: Array,
            default: () => [],
        },
        guestUsers: {
            type: Array,
            default: () => [],
        },
        selectedInternalIds: {
            type: Array,
            default: () => [],
        },
        selectedCustomerIds: {
            type: Array,
            default: () => [],
        },
        selectedGuestIds: {
            type: Array,
            default: () => [],
        },
        selectedInternalPermissions: {
            type: Object,
            default: () => ({}),
        },
        selectedCustomerPermissions: {
            type: Object,
            default: () => ({}),
        },
        readLabel: {
            type: String,
            default: 'Lesen',
        },
        editLabel: {
            type: String,
            default: 'Bearbeiten',
        },
        showCustomerTab: {
            type: Boolean,
            default: true,
        },
        showGuestTab: {
            type: Boolean,
            default: true,
        },
        initialTab: {
            type: String,
            default: 'internal',
        },
        internalTabLabel: {
            type: String,
            default: 'Interne Freigabe',
        },
        customerTabLabel: {
            type: String,
            default: 'Kundenfreigabe',
        },
        guestTabLabel: {
            type: String,
            default: 'Gastfreigabe',
        },
        emptyInternalText: {
            type: String,
            default: 'Keine internen Anwender vorhanden.',
        },
        emptyCustomerText: {
            type: String,
            default: 'Keine Kundenanwender vorhanden.',
        },
        emptyGuestText: {
            type: String,
            default: 'Keine Gastanwender vorhanden.',
        },
        saveLabel: {
            type: String,
            default: 'Speichern',
        },
        cancelLabel: {
            type: String,
            default: 'Abbrechen',
        },
    },
    data() {
        return {
            activeTab: 'internal',
            draftInternalIds: [],
            draftCustomerIds: [],
            draftGuestIds: [],
            draftInternalPermissions: {},
            draftCustomerPermissions: {},
        };
    },
    watch: {
        open(value) {
            if (value) {
                this.resetDraftState();
            }
        },
        showCustomerTab() {
            this.ensureAvailableTab();
        },
        showGuestTab() {
            this.ensureAvailableTab();
        },
    },
    methods: {
        approvalUserAvatar(user) {
            if (user && user.avatar && user.avatar !== 'gravatar') {
                return user.avatar;
            }

            return (user && user.gravatar) ? user.gravatar : '/images/default/avatar.svg';
        },
        resetDraftState() {
            this.draftInternalIds = this.normalizeIds(this.selectedInternalIds);
            this.draftCustomerIds = this.normalizeIds(this.selectedCustomerIds);
            this.draftGuestIds = this.normalizeIds(this.selectedGuestIds);
            this.draftInternalPermissions = this.normalizePermissionMap(
                this.selectedInternalPermissions,
                this.selectedInternalIds
            );
            this.draftCustomerPermissions = this.normalizePermissionMap(
                this.selectedCustomerPermissions,
                this.selectedCustomerIds
            );
            this.activeTab = this.initialTab;
            this.ensureAvailableTab();
        },
        ensureAvailableTab() {
            if (this.activeTab === 'customer' && !this.showCustomerTab) {
                this.activeTab = 'internal';
            }

            if (this.activeTab === 'guest' && !this.showGuestTab) {
                this.activeTab = this.showCustomerTab ? 'customer' : 'internal';
            }
        },
        normalizeIds(values) {
            return (Array.isArray(values) ? values : [])
                .map((value) => Number(value))
                .filter((value) => value > 0);
        },
        normalizePermissionMap(values, fallbackIds = []) {
            const permissions = {};
            Object.entries(values && typeof values === 'object' ? values : {}).forEach(([userId, permission]) => {
                const normalizedUserId = Number(userId);
                if (normalizedUserId > 0 && ['read', 'edit'].includes(permission)) {
                    permissions[normalizedUserId] = permission;
                }
            });

            if (Object.keys(permissions).length === 0) {
                this.normalizeIds(fallbackIds).forEach((userId) => {
                    permissions[userId] = 'read';
                });
            }

            return permissions;
        },
        permissionMap(group) {
            return group === 'customer' ? this.draftCustomerPermissions : this.draftInternalPermissions;
        },
        draftPermission(group, userId) {
            return this.permissionMap(group)[Number(userId)] || null;
        },
        hasReadPermission(group, userId) {
            return ['read', 'edit'].includes(this.draftPermission(group, userId));
        },
        hasEditPermission(group, userId) {
            return this.draftPermission(group, userId) === 'edit';
        },
        setReadPermission(group, userId, checked) {
            const map = this.permissionMap(group);
            const normalizedUserId = Number(userId);
            if (!checked) {
                delete map[normalizedUserId];
                return;
            }

            if (!map[normalizedUserId]) {
                map[normalizedUserId] = 'read';
            }
        },
        setEditPermission(group, userId, checked) {
            const map = this.permissionMap(group);
            const normalizedUserId = Number(userId);
            if (checked) {
                map[normalizedUserId] = 'edit';
                return;
            }

            if (map[normalizedUserId] === 'edit') {
                map[normalizedUserId] = 'read';
            }
        },
        isUserSelectable(user) {
            return !(user && (user.selectable === false || user.disabled === true));
        },
        filterSelectableIds(values, users) {
            const allowedIds = new Set(
                (Array.isArray(users) ? users : [])
                    .filter((user) => this.isUserSelectable(user))
                    .map((user) => Number(user.id))
                    .filter((id) => id > 0)
            );

            return this.normalizeIds(values).filter((id) => allowedIds.has(id));
        },
        filterSelectablePermissions(values, users) {
            const allowedIds = new Set(
                (Array.isArray(users) ? users : [])
                    .filter((user) => this.isUserSelectable(user))
                    .map((user) => Number(user.id))
                    .filter((id) => id > 0)
            );

            return Object.entries(values && typeof values === 'object' ? values : {})
                .map(([userId, permission]) => ({user_id: Number(userId), permission}))
                .filter((entry) => allowedIds.has(entry.user_id) && ['read', 'edit'].includes(entry.permission));
        },
        emitSave() {
            if (this.permissionMode) {
                const internalPermissions = this.filterSelectablePermissions(this.draftInternalPermissions, this.internalUsers);
                const customerPermissions = this.filterSelectablePermissions(this.draftCustomerPermissions, this.customerUsers);
                this.$emit('save', {
                    internal_permissions: internalPermissions,
                    customer_permissions: customerPermissions,
                    internal_ids: internalPermissions.map((entry) => entry.user_id),
                    customer_ids: customerPermissions.map((entry) => entry.user_id),
                    guest_ids: [],
                });
                return;
            }

            this.$emit('save', {
                internal_ids: this.filterSelectableIds(this.draftInternalIds, this.internalUsers),
                customer_ids: this.filterSelectableIds(this.draftCustomerIds, this.customerUsers),
                guest_ids: this.filterSelectableIds(this.draftGuestIds, this.guestUsers),
            });
        },
    },
};
</script>
